import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { type OfferteDienstInfo } from "@/data/offerte-diensten";

export interface OfferteData {
  klant: {
    naam: string;
    bedrijf: string;
    email: string;
    telefoon: string;
  };
  breakdown: { name: string; amount: number }[];
  total: number;
  eenmalig: number;
  datum: string;
  offerteNummer: string;
  logoBase64?: string;
  diensten?: { id: string; info: OfferteDienstInfo; qty: number }[];
}

const fmt = (n: number) => `€ ${n.toFixed(2).replace(".", ",")}`;

/* ── Colors ── */
const orange: [number, number, number] = [255, 107, 0];
const darkGray: [number, number, number] = [51, 51, 51];
const midGray: [number, number, number] = [120, 120, 120];
const lightBg: [number, number, number] = [248, 248, 248];

function drawHeaderFooter(doc: jsPDF) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFillColor(...orange);
  doc.rect(0, 0, pageW, 4, "F");
  doc.rect(0, pageH - 4, pageW, 4, "F");
}

/**
 * Generate a professional quote PDF and return as jsPDF doc
 */
export function generateOffertePDF(data: OfferteData): jsPDF {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentW = pageW - margin * 2;
  let y = margin;

  /* ── Header bar ── */
  drawHeaderFooter(doc);

  /* ── Logo + Company name ── */
  y = 20;
  if (data.logoBase64) {
    try {
      const imgProps = doc.getImageProperties(data.logoBase64);
      const logoW = 22;
      const logoH = (imgProps.height / imgProps.width) * logoW;
      doc.addImage(data.logoBase64, "PNG", margin, 10, logoW, logoH);
      y = 16;
    } catch {
      // Fallback: text only
      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      doc.setTextColor(...orange);
      doc.text("Rinqel", margin, y);
      doc.setTextColor(...darkGray);
      doc.text(".it", margin + doc.getTextWidth("Rinqel"), y);
    }
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(...orange);
    doc.text("Rinqel", margin, y);
    doc.setTextColor(...darkGray);
    doc.text(".it", margin + doc.getTextWidth("Rinqel"), y);
  }

  /* ── Company details (right side) ── */
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...midGray);
  const companyLines = [
    "Rinqel IT",
    "Rijksweg 42a",
    "1906 BJ Limmen",
    "085-8223400",
    "contact@rinqel.it",
  ];
  companyLines.forEach((line, i) => {
    doc.text(line, pageW - margin, 14 + i * 4, { align: "right" });
  });

  /* ── OFFERTE title ── */
  y = 40;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...darkGray);
  doc.text("Indicatieve offerte", margin, y);

  /* ── Meta info ── */
  y += 10;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...midGray);
  doc.text(`Offertenummer: ${data.offerteNummer}`, margin, y);
  doc.text(`Datum: ${data.datum}`, margin, y + 5);

  /* ── Klantgegevens ── */
  y += 16;
  doc.setFillColor(...lightBg);
  doc.roundedRect(margin, y, contentW, 30, 2, 2, "F");
  y += 7;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...darkGray);
  doc.text("Klantgegevens", margin + 6, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...midGray);
  const klantLines = [
    data.klant.naam,
    data.klant.bedrijf ? `${data.klant.bedrijf}` : "",
    `${data.klant.email}${data.klant.telefoon ? `  ·  ${data.klant.telefoon}` : ""}`,
  ].filter(Boolean);
  klantLines.forEach((line) => {
    doc.text(line, margin + 6, y);
    y += 4.5;
  });

  /* ── Diensten tabel ── */
  y += 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...darkGray);
  doc.text("Maandelijkse diensten", margin, y);
  y += 3;

  const tableData = data.breakdown.map((line) => [line.name, fmt(line.amount)]);

  autoTable(doc, {
    startY: y,
    head: [["Dienst", "Bedrag p/m"]],
    body: tableData,
    foot: [["Totaal per maand", fmt(data.total)]],
    theme: "plain",
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 9,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
      textColor: darkGray,
      lineColor: [220, 220, 220],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: midGray,
      fontStyle: "bold",
      fontSize: 8,
    },
    footStyles: {
      fillColor: orange,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 10,
    },
    columnStyles: {
      0: { cellWidth: contentW - 35 },
      1: { cellWidth: 35, halign: "right" },
    },
    didParseCell: (hookData) => {
      if (hookData.section === 'foot' && hookData.column.index === 1) {
        hookData.cell.styles.halign = 'right';
      }
    },
    didDrawPage: () => {
      drawHeaderFooter(doc);
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable?.finalY ?? y + 40;
  y += 8;

  /* ── Eenmalige kosten ── */
  if (data.eenmalig > 0) {
    doc.setFillColor(...lightBg);
    doc.roundedRect(margin, y, contentW, 16, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...midGray);
    doc.text("Eenmalige kosten", margin + 6, y + 6);
    doc.setFont("helvetica", "normal");
    doc.text("(implementatie & aansluitkosten)", margin + 6 + doc.getTextWidth("Eenmalige kosten  "), y + 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...darkGray);
    doc.text(fmt(data.eenmalig), pageW - margin - 6, y + 6, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...midGray);
    doc.text("Eenmalig, latere uitbreidingen kosteloos.", margin + 6, y + 12);
    y += 22;
  }

  /* ── Voorwaarden ── */
  y += 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...midGray);
  const disclaimers = [
    "• Alle prijzen zijn exclusief BTW.",
    "• Deze offerte is indicatief en vrijblijvend — neem contact met ons op voor een offerte op maat.",
    "• Maandelijks opzegbaar, geen langlopende contracten.",
    "• Prijzen zijn gebaseerd op de actuele tarieven op rinqel.it.",
  ];
  disclaimers.forEach((line) => {
    doc.text(line, margin, y);
    y += 4;
  });

  /* ── Footer bar (first page) ── */
  drawHeaderFooter(doc);

  /* ═══════════════════════════════════════════════
     DIENST-DETAIL PAGINA'S
     ═══════════════════════════════════════════════ */
  if (data.diensten && data.diensten.length > 0) {
    // Title page for details section
    doc.addPage();
    drawHeaderFooter(doc);
    y = 30;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(...darkGray);
    doc.text("Uw gekozen diensten", margin, y);
    y += 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...midGray);
    doc.text("Hieronder vindt u een toelichting op elke dienst die in deze offerte is opgenomen.", margin, y);
    y += 16;

    data.diensten.forEach((dienst) => {
      const info = dienst.info;
      const pageH = doc.internal.pageSize.getHeight();

      // Check if we need a new page (need at least ~80mm for a service block)
      if (y > pageH - 90) {
        doc.addPage();
        drawHeaderFooter(doc);
        y = 20;
      }

      // Accent bar
      doc.setFillColor(...orange);
      doc.rect(margin, y, 3, 20, "F");

      // Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(...darkGray);
      doc.text(info.title, margin + 8, y + 6);

      // Subtitle
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...orange);
      doc.text(info.subtitle, margin + 8, y + 12);

      // Quantity badge
      if (dienst.qty > 1) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...midGray);
        doc.text(`×${dienst.qty}`, pageW - margin, y + 6, { align: "right" });
      }

      y += 22;

      // Description — wrap text
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...darkGray);
      const descLines = doc.splitTextToSize(info.description, contentW - 8);
      doc.text(descLines, margin + 8, y);
      y += descLines.length * 4.2 + 4;

      // Highlights
      if (info.highlights.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...midGray);
        doc.text("Wat zit erin:", margin + 8, y);
        y += 5;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...darkGray);
        info.highlights.forEach((hl) => {
          if (y > pageH - 20) {
            doc.addPage();
            drawHeaderFooter(doc);
            y = 20;
          }
          doc.setFillColor(...orange);
          doc.circle(margin + 10.5, y - 1, 1, "F");
          doc.text(hl, margin + 14, y);
          y += 4.5;
        });
      }

      // Spacer between services
      y += 8;
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.3);
      doc.line(margin + 8, y - 4, pageW - margin, y - 4);
    });
  }

  return doc;
}

/**
 * Generate offerte nummer based on date
 */
export function generateOfferteNummer(): string {
  const now = new Date();
  const y = now.getFullYear().toString().slice(-2);
  const m = (now.getMonth() + 1).toString().padStart(2, "0");
  const d = now.getDate().toString().padStart(2, "0");
  const rand = Math.floor(Math.random() * 900 + 100);
  return `OFF-${y}${m}${d}-${rand}`;
}

/**
 * Load an image URL and return as base64 data URL
 */
export function loadImageAsBase64(src: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas not supported"));
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });
}
